// =================================================================
// Demo Datasets (Stage 5/10 — Demo Datasets)
// =================================================================
// Dataset count justification (N=7):
//
// The engine has distinct result modes across multiple dimensions:
//   1. Strategy: ExtendedInterval, Traditional, Synergy, Dialysis
//   2. EI interval ladder: q24h (CrCl≥60), q36h (CrCl 40-59), q48h (CrCl 20-39)
//   3. Weight selection: UseABW, UseIBW, UseAdjBW (>120% IBW)
//   4. Agent: Gentamicin/Tobramycin vs Amikacin (3x scaling)
//   5. Confidence: Empiric (no levels) vs LevelInformed (Hartford nomogram)
//
// Seven datasets cover every distinct branch:
//   #1 Standard EI q24h       — minimal/simple, normal renal, Empiric
//   #2 EI q36h moderate renal — interval ladder branch (CrCl 40-59)
//   #3 EI with Hartford level — LevelInformed confidence, nomogram classification
//   #4 Traditional dosing     — loading + maintenance + peak/trough prediction
//   #5 Obese patient (AdjBW)  — adjusted body weight selection path
//   #6 Amikacin EI            — different agent, 3x target scaling
//   #7 Synergy (endocarditis) — synergy strategy, lower peak targets, q12h
// =================================================================
import {
  Agent,
  FluidStatus,
  Indication,
  NomogramSource,
  PKSource,
  PopulationMode,
  Sex,
  Strategy,
} from '@/types';
import type { DemoDataset } from '@/store/types';

const NOW = '2025-06-15T08:00:00Z';

export const DEMO_DATASETS: DemoDataset[] = [
  {
    name: 'Standard EI — q24h',
    description:
      'Healthy 55-year-old male with normal renal function (CrCl ~83). Gentamicin extended-interval dosing at q24h. Simplest case demonstrating the standard EI pathway.',
    patient: {
      ageYears: 55,
      sex: Sex.Male,
      heightCm: 175,
      actualWeightKg: 80,
      scrMgDl: 1.0,
      scrTimestamp: NOW,
      indication: Indication.GramNegativeSepsis,
      fluidStatus: FluidStatus.Euvolemic,
      bunMgDl: 15,
    },
    strategyConfig: {
      strategy: Strategy.ExtendedInterval,
      infusionDurationMin: 30,
      infusionStartISO: undefined,
      nomogramSource: NomogramSource.Hartford,
      pkSource: PKSource.Population,
      populationMode: PopulationMode.Standard,
    },
    agent: Agent.Gentamicin,
  },
  {
    name: 'EI q36h — Moderate Renal Impairment',
    description:
      '72-year-old male with moderate renal impairment (CrCl ~45). Gentamicin EI dosing at q36h interval. Exercises the mid-range interval ladder branch.',
    patient: {
      ageYears: 72,
      sex: Sex.Male,
      heightCm: 170,
      actualWeightKg: 75,
      scrMgDl: 1.4,
      scrTimestamp: NOW,
      indication: Indication.Pyelonephritis,
      fluidStatus: FluidStatus.Euvolemic,
      bunMgDl: 28,
    },
    strategyConfig: {
      strategy: Strategy.ExtendedInterval,
      infusionDurationMin: 30,
      infusionStartISO: undefined,
      nomogramSource: NomogramSource.Hartford,
      pkSource: PKSource.Population,
      populationMode: PopulationMode.Standard,
    },
    agent: Agent.Gentamicin,
  },
  {
    name: 'EI with Hartford Level — Level-Informed',
    description:
      'Standard EI patient with a Hartford random level drawn at 8h (5.0 mg/L, in-window). Demonstrates nomogram classification and LevelInformed confidence.',
    patient: {
      ageYears: 55,
      sex: Sex.Male,
      heightCm: 175,
      actualWeightKg: 80,
      scrMgDl: 1.0,
      scrTimestamp: NOW,
      indication: Indication.GramNegativeSepsis,
      fluidStatus: FluidStatus.Euvolemic,
      bunMgDl: 15,
    },
    strategyConfig: {
      strategy: Strategy.ExtendedInterval,
      infusionDurationMin: 30,
      infusionStartISO: undefined,
      nomogramSource: NomogramSource.Hartford,
      pkSource: PKSource.Population,
      populationMode: PopulationMode.Standard,
    },
    agent: Agent.Gentamicin,
    hartfordLevel: { valueMg: 5.0, drawTimeHr: 8 },
  },
  {
    name: 'Traditional Dosing',
    description:
      '60-year-old female on traditional divided dosing. Demonstrates loading dose, maintenance dose, and predicted steady-state peak/trough from Kel/Vd.',
    patient: {
      ageYears: 60,
      sex: Sex.Female,
      heightCm: 160,
      actualWeightKg: 65,
      scrMgDl: 1.2,
      scrTimestamp: NOW,
      indication: Indication.ComplicatedUTI,
      fluidStatus: FluidStatus.Euvolemic,
      bunMgDl: 20,
    },
    strategyConfig: {
      strategy: Strategy.Traditional,
      infusionDurationMin: 30,
      infusionStartISO: undefined,
      nomogramSource: NomogramSource.Hartford,
      pkSource: PKSource.Population,
      populationMode: PopulationMode.Standard,
    },
    agent: Agent.Gentamicin,
  },
  {
    name: 'Obese Patient — AdjBW',
    description:
      '45-year-old male, BMI ~37.8 (120 kg, 178 cm). Adjusted body weight is used for dosing. Demonstrates the AdjBW selection rule (>120% IBW) with augmented clearance.',
    patient: {
      ageYears: 45,
      sex: Sex.Male,
      heightCm: 178,
      actualWeightKg: 120,
      scrMgDl: 0.9,
      scrTimestamp: NOW,
      indication: Indication.IntraAbdominal,
      fluidStatus: FluidStatus.Euvolemic,
      bunMgDl: 12,
    },
    strategyConfig: {
      strategy: Strategy.ExtendedInterval,
      infusionDurationMin: 30,
      infusionStartISO: undefined,
      nomogramSource: NomogramSource.Hartford,
      pkSource: PKSource.Population,
      populationMode: PopulationMode.Obesity,
    },
    agent: Agent.Gentamicin,
  },
  {
    name: 'Amikacin EI',
    description:
      '50-year-old male treated with amikacin. Demonstrates the amikacin agent with 3x target scaling (peak 24-36, trough 1.5-6) and 100 mg rounding increment.',
    patient: {
      ageYears: 50,
      sex: Sex.Male,
      heightCm: 175,
      actualWeightKg: 78,
      scrMgDl: 1.1,
      scrTimestamp: NOW,
      indication: Indication.Bacteremia,
      fluidStatus: FluidStatus.Euvolemic,
      bunMgDl: 18,
    },
    strategyConfig: {
      strategy: Strategy.ExtendedInterval,
      infusionDurationMin: 30,
      infusionStartISO: undefined,
      nomogramSource: NomogramSource.Hartford,
      pkSource: PKSource.Population,
      populationMode: PopulationMode.Standard,
    },
    agent: Agent.Amikacin,
  },
  {
    name: 'Synergy — Endocarditis',
    description:
      '68-year-old male with enterococcal endocarditis on synergy dosing. Lower peak targets (3-4 mg/L), q12h divided dosing. Exercises the synergy strategy path.',
    patient: {
      ageYears: 68,
      sex: Sex.Male,
      heightCm: 172,
      actualWeightKg: 70,
      scrMgDl: 1.3,
      scrTimestamp: NOW,
      indication: Indication.Endocarditis,
      fluidStatus: FluidStatus.Euvolemic,
      bunMgDl: 24,
    },
    strategyConfig: {
      strategy: Strategy.Synergy,
      infusionDurationMin: 30,
      infusionStartISO: undefined,
      nomogramSource: NomogramSource.Hartford,
      pkSource: PKSource.Population,
      populationMode: PopulationMode.Standard,
    },
    agent: Agent.Gentamicin,
  },
];
